/**
 * ComparisonOverview — high-level summary panel for the Comparison view.
 *
 *   ┌────────────────┬──────────┬──────────┬──────────┐
 *   │                │ Site A   │ Site B   │ Site C   │  ← column header
 *   │                │ photo    │ photo    │ photo    │    (image + status)
 *   ├────────────────┼──────────┼──────────┼──────────┤
 *   │ Address        │ …        │ …        │ …        │
 *   │ Sqft           │ …        │ …        │ …        │
 *   │ Ranking        │ #1       │ #3       │ #2       │
 *   │ Top criterion  │ …        │ …        │ …        │
 *   │ Top risk       │ …        │ …        │ …        │
 *   │ NPV (headline) │ …        │ —        │ —        │
 *   └────────────────┴──────────┴──────────┴──────────┘
 *
 * Layout uses a CSS grid with the first column (row labels) sticky to the
 * left edge of an `overflow-x-auto` strip so labels stay visible while the
 * site columns scroll horizontally on mobile.
 *
 * Data sources:
 *   - site fields              EvaluationSite entities (already in store)
 *   - ranking + top criterion  state.scoring.summaryByEvaluation[evalId]
 *   - top risk                 state.risks.comparisonByEvaluation[evalId]
 *                                (the panel dispatches fetchRisksComparison)
 *   - NPV                      state.siteFinancials.metricsBySite[siteId]
 *                                .ROI_ANALYSIS.rollup.npv (populated by F4)
 *
 * Linear: MYD-217 (F2).
 */

import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchRisksComparison } from 'slices/riskSlice'
import ComparisonRowLabel from './ComparisonRowLabel'
import ComparisonSiteHeader from './ComparisonSiteHeader'
import { renderDash, gridTemplate } from './comparisonShared'

interface ComparisonOverviewProps {
	evalId: string
	sites: any[]
}

const ComparisonOverview = ({ evalId, sites }: ComparisonOverviewProps) => {
	const dispatch = useDispatch()
	const summary = useSelector((state: any) => state.scoring?.summaryByEvaluation?.[evalId])
	const risksComparison: any[] = useSelector(
		(state: any) => state.risks?.comparisonByEvaluation?.[evalId] || [],
	)
	const metricsBySite: Record<string, Record<string, any>> = useSelector(
		(state: any) => state.siteFinancials?.metricsBySite || {},
	)

	useEffect(() => {
		;(dispatch as any)(fetchRisksComparison(evalId))
	}, [dispatch, evalId])

	const rankingsBySiteId: Record<string, number> = useMemo(() => {
		if (!summary?.sites?.length) return {}
		const sorted = summary.sites
			.slice()
			.sort((a: any, b: any) => (b.overallScore ?? -Infinity) - (a.overallScore ?? -Infinity))
		const map: Record<string, number> = {}
		sorted.forEach((row: any, idx: number) => {
			map[String(row.siteId)] = idx + 1
		})
		return map
	}, [summary])

	const topCriterionBySiteId: Record<string, string> = useMemo(() => {
		const out: Record<string, string> = {}
		for (const s of summary?.sites ?? []) {
			const best = (s.criteria ?? [])
				.filter((c: any) => c.avgScore != null)
				.sort((a: any, b: any) => (b.avgScore ?? 0) - (a.avgScore ?? 0))[0]
			if (best) out[String(s.siteId)] = best.name
		}
		return out
	}, [summary])

	const topRiskBySiteId: Record<string, any> = useMemo(() => {
		const out: Record<string, any> = {}
		for (const row of risksComparison) {
			const top = (row.risks ?? [])
				.filter((r: any) => r.status === 'OPEN' || r.status === 'MITIGATING')
				.sort((a: any, b: any) => (b.riskScore ?? 0) - (a.riskScore ?? 0))[0]
			if (top) out[String(row.siteId)] = top
		}
		return out
	}, [risksComparison])

	const npvBySiteId: Record<string, number | null> = useMemo(() => {
		const out: Record<string, number | null> = {}
		for (const site of sites) {
			const rollup = metricsBySite[site._id]?.ROI_ANALYSIS?.rollup
			out[site._id] = rollup?.npv ?? null
		}
		return out
	}, [metricsBySite, sites])

	const fmtAddress = (s: any) =>
		[s.address, s.city, s.state].filter(Boolean).join(', ') || null
	const fmtSqft = (s: any) => (s.sqft ? `${Number(s.sqft).toLocaleString()} sqft` : null)
	const fmtNpv = (n: number | null) =>
		n == null ? null : `$${Math.round(n).toLocaleString()}`

	const rows: { label: string; render: (site: any) => React.ReactNode }[] = [
		{ label: 'Address', render: (s) => renderDash(fmtAddress(s)) },
		{ label: 'Sqft', render: (s) => renderDash(fmtSqft(s)) },
		{
			label: 'Ranking',
			render: (s) => {
				const rank = rankingsBySiteId[s._id]
				return rank ? <span className="font-semibold text-[#0080fe]">#{rank}</span> : '—'
			},
		},
		{
			label: 'Top scoring criterion',
			render: (s) => renderDash(topCriterionBySiteId[s._id]),
		},
		{
			label: 'Top open risk',
			render: (s) => {
				const r = topRiskBySiteId[s._id]
				if (!r) return '—'
				return (
					<span>
						<span className="font-medium text-gray-800">{r.name}</span>
						<span className="block text-[10px] text-gray-500">
							score {r.riskScore} · {r.status}
						</span>
					</span>
				)
			},
		},
		{
			label: 'NPV (headline)',
			render: (s) => renderDash(fmtNpv(npvBySiteId[s._id])),
		},
	]

	return (
		<div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
			<div
				className="grid"
				style={{ gridTemplateColumns: gridTemplate(sites.length), minWidth: '100%' }}
			>
				{/* Header row */}
				<div className="bg-gray-50 border-b border-gray-200 px-3 py-2 sticky left-0 z-10" />
				{sites.map((site) => (
					<ComparisonSiteHeader key={site._id} site={site} />
				))}

				{/* Data rows */}
				{rows.map((row, idx) => (
					<div className="contents" key={row.label}>
						<ComparisonRowLabel
							label={row.label}
							className={idx % 2 === 1 ? 'bg-gray-50/60' : 'bg-white'}
						/>
						{sites.map((site) => (
							<div
								key={site._id}
								className={`px-3 py-2 text-sm text-gray-700 border-t border-gray-100 ${
									idx % 2 === 1 ? 'bg-gray-50/60' : 'bg-white'
								}`}
							>
								{row.render(site)}
							</div>
						))}
					</div>
				))}
			</div>
		</div>
	)
}

export default ComparisonOverview
